import {Chips, DataStatus} from 'platform/components';
import {VStack} from 'platform/foundation';

import {isArray, isEmptyArray} from 'ramda-adjunct';

import {suffixTestId, useOnMount, type Nullish} from 'shared/web';

import {FILTER_VALUE_KEY} from '../../constants/filterValueKey';
import {useChoiceFilter} from '../../hooks/useChoiceFilter';
import {useLazyChoiceFilterOptions} from '../../hooks/useLazyChoiceFilterOptions';
import type {EagFilterProps} from '../../types/EagFilterProps';
import {getQuickFilterFromIntersection} from '../../utils/quickFilterValue';
import {QuickFilterChips} from '../QuickFilterChips/QuickFilterChips';

type ChipsFilterProps = EagFilterProps & {
  columnKey: string;
  quickFilters?: Array<{value: string; label: string}>;
};

export function ChipsFilter(props: ChipsFilterProps) {
  const rawModel = props.value[FILTER_VALUE_KEY] as string[] | string | Nullish;

  const [values, setValues] = useChoiceFilter({
    model: rawModel,
    onChange: (newValue) => {
      props.onChange({[FILTER_VALUE_KEY]: newValue});
    },
  });

  const {loadOptions, isLoading, options, errorMessage} = useLazyChoiceFilterOptions({
    columnKey: props.columnKey,
  });

  const isQuickFilterValue = getQuickFilterFromIntersection(values);
  const isControlDisabled = props.isDisabled;

  useOnMount(() => {
    void loadOptions();
  });

  return (
    // spacing keeps the quick-filter chip strip from touching the option chips —
    // without it the two chip rows render with no vertical gap (T20-134467).
    <VStack spacing={2}>
      <QuickFilterChips
        quickFilters={props.quickFilters}
        value={isQuickFilterValue}
        defaultValue={null}
        setFilterValue={setValues}
        isDisabled={props.isDisabled}
        data-testid={suffixTestId('chips-filter-quickFilters', props)}
      />
      <DataStatus isLoading={isLoading}>
        <Chips
          isDisabled={isControlDisabled}
          options={options}
          value={isEmptyArray(isQuickFilterValue) && isArray(values) ? values : null}
          onChange={setValues}
          isMultiple
          isDeselectable
          errorMessage={errorMessage}
          data-testid={suffixTestId('chips-filter', props)}
        />
      </DataStatus>
    </VStack>
  );
}
